import { useState, useEffect } from 'react';
import { KidProfile, useKidsStore } from '../../store/kidsStore';
import { useTenant } from '../../contexts/TenantContext';

interface KidProfileCardProps {
  profile: KidProfile;
  onEdit: (profile: KidProfile) => void;
  onDelete: (profile: KidProfile) => void;
}

export default function KidProfileCard({ profile, onEdit, onDelete }: KidProfileCardProps) {
  const tenant = useTenant();
  const { getStats, canPlay } = useKidsStore();
  const [stats, setStats] = useState({ total_partidas: 0, juegos_jugados: 0, tiempo_total_minutos: 0 });
  const [playStatus, setPlayStatus] = useState({ tiempo_jugado: 0, tiempo_limite: 0 });

  useEffect(() => {
    loadData();
  }, [profile.id]);

  const loadData = async () => {
    try {
      const [statsData, playData] = await Promise.all([
        getStats(profile.id),
        canPlay(profile.id)
      ]);
      setStats(statsData);
      setPlayStatus({
        tiempo_jugado: playData.tiempo_jugado,
        tiempo_limite: playData.tiempo_limite
      });
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  return (
    <div
      className="rounded-2xl p-6 shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
      style={{
        background: 'white',
        border: '1px solid rgba(0,0,0,0.1)'
      }}
    >
      {/* Header con avatar y nombre */}
      <div className="flex items-center gap-4 mb-4">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-4xl"
          style={{
            background: tenant.theme.colors.gradients.main
          }}
        >
          {profile.avatar}
        </div>
        <div>
          <h3
            className="text-xl font-bold"
            style={{ color: tenant.theme.colors.primary }}
          >
            {profile.nombre}
          </h3>
          <p className="text-gray-600">{profile.edad} años</p>
        </div>
      </div>

      {/* Control de tiempo */}
      <div
        className="p-3 rounded-lg mb-4"
        style={{
          background: '#FFF9E6',
          borderLeft: `4px solid ${tenant.theme.colors.accent}`
        }}
      >
        <p className="text-sm text-gray-600 mb-1">Tiempo hoy</p>
        <p className="text-lg font-semibold" style={{ color: tenant.theme.colors.primary }}>
          {playStatus.tiempo_jugado} / {playStatus.tiempo_limite} min
        </p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div
            className="text-2xl font-bold"
            style={{ color: tenant.theme.colors.primary }}
          >
            {stats.total_partidas}
          </div>
          <div className="text-xs text-gray-600">Partidas</div>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div
            className="text-2xl font-bold"
            style={{ color: tenant.theme.colors.primary }}
          >
            {stats.juegos_jugados}
          </div>
          <div className="text-xs text-gray-600">Juegos</div>
        </div>
      </div>

      {/* Acciones */}
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(profile)}
          className="flex-1 py-2 px-4 rounded-lg font-semibold transition-all hover:opacity-80"
          style={{
            background: tenant.theme.colors.primary,
            color: 'white'
          }}
        >
          ✏️ Editar
        </button>
        <button
          onClick={() => onDelete(profile)}
          className="py-2 px-4 rounded-lg font-semibold transition-all hover:opacity-80"
          style={{
            background: '#ff4444',
            color: 'white'
          }}
        >
          🗑️
        </button>
      </div>
    </div>
  );
}
